import { SIZER_POINTS } from "@/models/node/SIZER_POINTS";
import { Scale } from "@/services/canvas/Scale";
import type {
  SelectionElement,
  RenderData,
  SelectionPoint,
  BoxMeasurements,
  SelectionData,
  BoxData,
  DotData
} from "../types/SelectionTypes";
import type { SelectionBox } from "../types/PointerTypes";

/**
 * Service responsible for rendering selection highlights on the canvas.
 * Handles single-selection (solid border + sizer dots), multi-selection
 * (individual solid borders + dotted outer bounding box + sizer dots),
 * and lasso drag-selection rectangle.
 */
export class SelectionRenderService {

  /**
   * Calculate the four corner points of an element's axis-aligned bounding box.
   *
   * @param element - The element to calculate bounds for
   * @returns Array of four corner points
   */
  calculateElementPoints(element: SelectionElement): SelectionPoint[] {
    const computedBorder = element.border && element.borderWidth ? element.borderWidth * 2 : 0;
    const rectWidth = element.width + computedBorder;
    const rectHeight = element.height + computedBorder;

    return [
      { x: element.x - rectWidth / 2, y: element.y - rectHeight / 2 },
      { x: element.x + rectWidth / 2, y: element.y - rectHeight / 2 },
      { x: element.x + rectWidth / 2, y: element.y + rectHeight / 2 },
      { x: element.x - rectWidth / 2, y: element.y + rectHeight / 2 },
    ];
  }

  /**
   * Calculate the outer bounding box encompassing all elements.
   *
   * @param elements - Selected elements
   * @returns Box measurements with corner points and dimensions, or null if empty
   */
  calculateBoxPoints(elements: SelectionElement[]): BoxMeasurements | null {
    if (elements.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const element of elements) {
      const points = this.calculateElementPoints(element);
      for (const p of points) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
    }

    return {
      points: [
        { x: minX, y: minY, position: SIZER_POINTS.TOP_LEFT, pointer: "nw-resize" },
        { x: maxX, y: minY, position: SIZER_POINTS.TOP_RIGHT, pointer: "ne-resize" },
        { x: maxX, y: maxY, position: SIZER_POINTS.BOTTOM_RIGHT, pointer: "se-resize" },
        { x: minX, y: maxY, position: SIZER_POINTS.BOTTOM_LEFT, pointer: "sw-resize" },
      ],
      selection: {
        minX,
        minY,
        width: maxX - minX,
        height: maxY - minY,
      },
    };
  }

  /**
   * Main render entry point. Renders single or multi-selection highlights.
   *
   * @param data - Render data with elements and canvas context
   */
  render(data: RenderData): void {
    const { elements, ctx, mapAreaX, mapAreaY, zoomLevel, hovered = false } = data;
    const box = this.calculateBoxPoints(elements);
    if (!box) return;

    const isMultiSelection = elements.length > 1;

    if (isMultiSelection) {
      // Draw solid border rect around each individual selected element
      for (const element of elements) {
        const elPoints = this.calculateElementPoints(element);
        const elMinX = Math.min(...elPoints.map(p => p.x));
        const elMinY = Math.min(...elPoints.map(p => p.y));
        const elMaxX = Math.max(...elPoints.map(p => p.x));
        const elMaxY = Math.max(...elPoints.map(p => p.y));

        this.renderRect({
          ctx,
          width: elMaxX - elMinX,
          height: elMaxY - elMinY,
          minX: elMinX,
          minY: elMinY,
          mapAreaX,
          mapAreaY,
          zoomLevel,
        });
      }

      // Draw dotted outer bounding box
      this.renderDottedRect({
        ctx,
        width: box.selection.width,
        height: box.selection.height,
        minX: box.selection.minX,
        minY: box.selection.minY,
        mapAreaX,
        mapAreaY,
        zoomLevel,
      }, hovered);

      // Draw sizer dots at outer bounding box corners
      for (const point of box.points) {
        this.renderDot({ ctx, point, mapAreaX, mapAreaY, zoomLevel });
      }
    } else {
      // Single selection — solid border + sizer dots (current behavior)
      this.renderSelection({
        points: box.points,
        selection: box.selection,
        ctx,
        mapAreaX,
        mapAreaY,
        zoomLevel,
      });
    }
  }

  /**
   * Render a single-element selection with solid border and sizer dots.
   *
   * @param data - Selection data with points and canvas context
   */
  renderSelection(data: SelectionData): void {
    const { ctx, selection, points, mapAreaX, mapAreaY, zoomLevel } = data;

    this.renderRect({
      ctx,
      width: selection.width,
      height: selection.height,
      minX: selection.minX,
      minY: selection.minY,
      mapAreaX,
      mapAreaY,
      zoomLevel,
    });

    for (const point of points) {
      this.renderDot({ ctx, point, mapAreaX, mapAreaY, zoomLevel });
    }
  }

  /**
   * Render a sizer dot at a corner point.
   *
   * @param data - Dot rendering data
   */
  renderDot({ ctx, point, mapAreaX, mapAreaY, zoomLevel }: DotData): void {
    const DOT_RADIUS = 3 * zoomLevel;
    const x = Scale.x(point.x + mapAreaX, ctx.canvas.width / 2, zoomLevel);
    const y = Scale.y(point.y + mapAreaY, ctx.canvas.height / 2, zoomLevel);

    ctx.beginPath();
    ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
    ctx.closePath();

    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#2750E2";
    ctx.lineWidth = zoomLevel;
    ctx.fill();
    ctx.stroke();
  }

  /**
   * Render a solid-stroke rectangle.
   *
   * @param data - Box rendering data
   */
  renderRect({ ctx, width, height, minX, minY, mapAreaX, mapAreaY, zoomLevel }: BoxData): void {
    const x = Scale.x(minX + mapAreaX, ctx.canvas.width / 2, zoomLevel);
    const y = Scale.y(minY + mapAreaY, ctx.canvas.height / 2, zoomLevel);

    ctx.beginPath();
    ctx.rect(x, y, width * zoomLevel, height * zoomLevel);
    ctx.closePath();
    ctx.strokeStyle = "#2750E2";
    ctx.lineWidth = zoomLevel;
    ctx.stroke();
  }

  /**
   * Render a dotted-stroke rectangle for multi-selection outer bounding box.
   *
   * @param data - Box rendering data
   */
  renderDottedRect({ ctx, width, height, minX, minY, mapAreaX, mapAreaY, zoomLevel }: BoxData, hovered = false): void {
    const x = Scale.x(minX + mapAreaX, ctx.canvas.width / 2, zoomLevel);
    const y = Scale.y(minY + mapAreaY, ctx.canvas.height / 2, zoomLevel);

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, width * zoomLevel, height * zoomLevel);
    ctx.closePath();

    // Semi-transparent fill — brighter when hovered for visual feedback
    ctx.fillStyle = hovered ? "rgba(39, 80, 226, 0.12)" : "rgba(39, 80, 226, 0.05)";
    ctx.fill();

    // Dotted border
    ctx.setLineDash([6 * zoomLevel, 4 * zoomLevel]);
    ctx.strokeStyle = "#2750E2";
    ctx.lineWidth = zoomLevel;
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Render the lasso drag-selection rectangle with dotted border and semi-transparent fill.
   *
   * @param ctx          - Canvas 2D context
   * @param selectionBox - Lasso rectangle bounds in canvas coordinates
   */
  renderLassoRect(ctx: CanvasRenderingContext2D, selectionBox: SelectionBox): void {
    const x = Math.min(selectionBox.startX, selectionBox.endX);
    const y = Math.min(selectionBox.startY, selectionBox.endY);
    const width = Math.abs(selectionBox.endX - selectionBox.startX);
    const height = Math.abs(selectionBox.endY - selectionBox.startY);

    ctx.save();

    // Semi-transparent fill
    ctx.fillStyle = "rgba(39, 80, 226, 0.08)";
    ctx.fillRect(x, y, width, height);

    // Dotted border
    ctx.beginPath();
    ctx.setLineDash([6, 4]);
    ctx.rect(x, y, width, height);
    ctx.closePath();
    ctx.strokeStyle = "#2750E2";
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore();
  }
}
